/**
 * Organization utilities for multi-tenant routing
 * Resolves shortCodes and Clerk org IDs via API with an in-memory cache, backed by a saved
 * mapping (`org-resolve-storage`) so a reload opens the salon without waiting on the network.
 */
import {
  forgetStoredOrgResolution,
  readStoredOrgResolution,
  storeOrgResolution,
  type OrgResolution,
} from './org-resolve-storage';

/** Cache for resolved orgs (avoids repeated API calls) */
const resolveCache = new Map<string, OrgResolution>();
/** Saved mappings already re-checked against the API during this page load. */
const revalidatedIdentifiers = new Set<string>();
const resolveInvalidationListeners = new Set<() => void>();

/**
 * Called when a saved mapping turned out wrong (salon deleted, or the code now names another salon) after it was
 * already used. The router re-runs its org check then, which redirects exactly as a fresh open would.
 */
export function subscribeOrgResolveInvalidation(listener: () => void): () => void {
  resolveInvalidationListeners.add(listener);
  return () => resolveInvalidationListeners.delete(listener);
}

function cacheOrgResolution(identifier: string, result: OrgResolution) {
  resolveCache.set(identifier, result);
  resolveCache.set(result.orgId, result);
  resolveCache.set(result.shortCode, result);
}

function dropOrgResolution(identifier: string, result: OrgResolution) {
  for (const key of [identifier, result.orgId, result.shortCode]) {
    resolveCache.delete(key);
    forgetStoredOrgResolution(key);
  }
}

/**
 * Pre-fill the resolve cache from an authoritative list that already carries
 * both identifiers (e.g. the agency salon switcher), so navigating to one of
 * those salons skips the public resolve round-trip in the route `beforeLoad`.
 */
export function seedOrgResolveCache({ orgId, shortCode }: { orgId: string; shortCode: string }) {
  const result = { orgId, shortCode };
  resolveCache.set(orgId, result);
  resolveCache.set(shortCode, result);
}

/**
 * Distinguishes a genuine "organization does not exist" (HTTP 404) from a
 * transient failure (offline, connection reset on wake, dev server not ready,
 * 5xx). Callers must NOT sign the user out on a transient failure — the session
 * and workspace are usually fine and a retry recovers.
 */
export class OrgResolutionError extends Error {
  constructor(
    message: string,
    /** 'not-found' = org truly gone (404); 'network' = transient, safe to retry. */
    readonly kind: 'not-found' | 'network'
  ) {
    super(message);
    this.name = 'OrgResolutionError';
  }
}

/** True only when the org genuinely does not exist (404), not a transient blip. */
export function isOrgNotFoundError(error: unknown): boolean {
  return error instanceof OrgResolutionError && error.kind === 'not-found';
}

const RESOLVE_MAX_ATTEMPTS = 3;
const RESOLVE_RETRY_BASE_DELAY_MS = 300;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolve any org identifier (shortCode or org_xxx) to { orgId, shortCode }.
 * Results are cached by both identifier and orgId for fast lookups.
 *
 * A mapping saved by an earlier page load answers at once (stale-while-revalidate): it is re-checked in the
 * background once per page load, and a 404 or a different answer drops it and re-runs the route's org check.
 */
export async function resolveOrgIdentifier(identifier: string): Promise<OrgResolution> {
  const cached = resolveCache.get(identifier);
  if (cached) return cached;

  const stored = readStoredOrgResolution(identifier);
  if (stored) {
    cacheOrgResolution(identifier, stored);
    revalidateStoredOrgResolution(identifier, stored);
    return stored;
  }

  const result = await fetchOrgResolution(identifier);
  cacheOrgResolution(identifier, result);
  storeOrgResolution(result);
  return result;
}

function revalidateStoredOrgResolution(identifier: string, stored: OrgResolution) {
  if (revalidatedIdentifiers.has(identifier)) return;
  revalidatedIdentifiers.add(identifier);
  void fetchOrgResolution(identifier).then(
    (fresh) => {
      const unchanged = fresh.orgId === stored.orgId && fresh.shortCode === stored.shortCode;
      if (!unchanged) dropOrgResolution(identifier, stored);
      cacheOrgResolution(identifier, fresh);
      storeOrgResolution(fresh);
      if (!unchanged) resolveInvalidationListeners.forEach((listener) => listener());
    },
    (error: unknown) => {
      // A transient failure keeps the saved mapping (it never changes for a live salon); only a 404 proves it wrong.
      if (!isOrgNotFoundError(error)) return;
      dropOrgResolution(identifier, stored);
      resolveInvalidationListeners.forEach((listener) => listener());
    }
  );
}

/**
 * Resilient to transient failures: retries network errors and 5xx/429 with
 * exponential backoff so a single blip (e.g. right after the laptop wakes from
 * sleep) does not tear down the user's session. A real 404 fails fast.
 */
async function fetchOrgResolution(identifier: string): Promise<OrgResolution> {
  let lastError: unknown;

  for (let attempt = 0; attempt < RESOLVE_MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await wait(RESOLVE_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
    }

    let response: Response;
    try {
      response = await fetch(
        `${import.meta.env.VITE_API_URL}/public/organizations/resolve/${identifier}`
      );
    } catch {
      // Fetch rejected (offline, DNS, connection reset on wake) — transient, retry.
      lastError = new OrgResolutionError(
        `Network error resolving organization "${identifier}"`,
        'network'
      );
      continue;
    }

    if (response.status === 404) {
      // Organization genuinely does not exist — do not retry.
      throw new OrgResolutionError(`Organization not found: ${identifier}`, 'not-found');
    }

    if (!response.ok) {
      // 5xx / 429 / other server hiccup — transient, retry.
      lastError = new OrgResolutionError(
        `Organization resolve failed (status ${response.status}) for "${identifier}"`,
        'network'
      );
      continue;
    }

    const data = await response.json();
    return { orgId: data.organizationId, shortCode: data.shortCode };
  }

  throw (
    lastError ??
    new OrgResolutionError(
      `Organization resolve failed after ${RESOLVE_MAX_ATTEMPTS} attempts for "${identifier}"`,
      'network'
    )
  );
}

export function isShortCode(identifier: string): boolean {
  return /^\d{1,6}$/.test(identifier);
}

export function isClerkOrgId(identifier: string): boolean {
  return /^org_[a-zA-Z0-9]+$/.test(identifier);
}

/**
 * The same page under the salon's short code: `/org_abc/turn-tracker?x=1` → `/12345/turn-tracker?x=1`. Links that
 * only know the Clerk organization id (the mobile app's "Open Turns on the web") keep their page after the redirect.
 */
export function shortCodeOrgHref(
  location: { pathname: string; searchStr?: string; hash?: string },
  identifier: string,
  shortCode: string
): string {
  const prefix = `/${identifier}`;
  const rest = location.pathname.startsWith(prefix) ? location.pathname.slice(prefix.length) : '';
  const hash = location.hash ? `#${location.hash.replace(/^#/, '')}` : '';
  return `/${shortCode}${rest}${location.searchStr ?? ''}${hash}`;
}

type ClerkOrganizationMembershipLike = {
  organization?: {
    id?: string | null;
  } | null;
};

export function hasClerkOrganizationMembership(
  memberships: ClerkOrganizationMembershipLike[] | null | undefined,
  organizationId: string
): boolean {
  return memberships?.some((membership) => membership.organization?.id === organizationId) ?? false;
}

export type SignedInWorkspaceDestination =
  | { type: 'organization'; shortCode: string }
  | { type: 'choose-salon' }
  | { type: 'agency' }
  | { type: 'pending' }
  | { type: 'unavailable' };

/**
 * Where a signed-in person lands at `/`: their one salon, the "Choose a salon"
 * screen when they work in 2+ salons, the agency workspace, or the "access
 * needed" card. `staffSalons` is the person's active staff salons (undefined
 * while loading); archived staff therefore never reach agency routing by mistake.
 */
export function getSignedInWorkspaceDestination({
  staffSalons,
  agencyIdentityResolved,
  hasAgencyAccess,
}: {
  staffSalons: ReadonlyArray<{ shortCode: string }> | undefined;
  agencyIdentityResolved: boolean;
  hasAgencyAccess: boolean;
}): SignedInWorkspaceDestination {
  // The API never answers an agency member for a login with an active staff row
  // (403 for dual access), so a confirmed member never needs the salon list.
  if (hasAgencyAccess) return { type: 'agency' };
  if (!staffSalons) return { type: 'pending' };
  if (staffSalons.length > 1) return { type: 'choose-salon' };
  const [onlySalon] = staffSalons;
  if (onlySalon) return { type: 'organization', shortCode: onlySalon.shortCode };
  return agencyIdentityResolved ? { type: 'unavailable' } : { type: 'pending' };
}
